import React, { useMemo, useRef, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
  ExportCmpReportCsvInput,
  useExportCmpReportCsvLazyQuery,
  useGetCmpReportLookupsQuery,
} from "../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../hooks/useGetChamberFromUserIdInAuth";
import { ManagePageShell } from "../../common/dashboard/ManagePageShell";
import { downloadTextFile } from "../Invoicing/invoiceFileDownloads";
import ReportFilters, {
  ReportFilterValues,
} from "./ReportFilters";
import ReportSelector from "./ReportSelector";
import {
  REPORT_DEFINITIONS,
  ReportDefinition,
  reportHasFilter,
} from "./reportDefinitions";

type StatusMessage = {
  severity: "success" | "error" | "info";
  message: string;
};

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const createDefaultFilterValues = (): ReportFilterValues => {
  const today = new Date();
  const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
  const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const compareStartDate = new Date(
    today.getFullYear() - 1,
    today.getMonth(),
    1,
  );
  const compareEndDate = new Date(
    today.getFullYear() - 1,
    today.getMonth() + 1,
    0,
  );

  return {
    startDate: toDateInputValue(startDate),
    endDate: toDateInputValue(endDate),
    compareStartDate: toDateInputValue(compareStartDate),
    compareEndDate: toDateInputValue(compareEndDate),
    filterDate: "",
    membershipTypeId: "",
    memberSize: "",
    membershipCategory: "",
    eventCode: "",
    eventId: "",
    eventTypeId: "",
    sponsorshipPackageId: "",
    ticketTypeId: "",
    advertisingCode: "",
    advertisingOptionId: "",
    leadCaptureId: "",
    createdByName: "",
    salesAccountNumber: "",
  };
};

const toUtcDayStart = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return Date.UTC(year, month - 1, day, 0, 0, 0, 0);
};

const toUtcDayEnd = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return Date.UTC(year, month - 1, day, 23, 59, 59, 999);
};

const getFilterValidationError = ({
  report,
  values,
}: {
  report: ReportDefinition;
  values: ReportFilterValues;
}) => {
  const dateValues = [
    values.startDate,
    values.endDate,
    values.compareStartDate,
    values.compareEndDate,
  ];

  if (dateValues.some((value) => !value)) {
    return "Complete all four report and comparison date fields before downloading.";
  }

  if (toUtcDayStart(values.startDate) > toUtcDayEnd(values.endDate)) {
    return "Report Start Date must be on or before Report End Date.";
  }

  if (
    toUtcDayStart(values.compareStartDate) >
    toUtcDayEnd(values.compareEndDate)
  ) {
    return "Compare To Start Date must be on or before Compare To End Date.";
  }

  if (reportHasFilter({ report, filter: "memberSize" }) && values.memberSize) {
    const memberSize = Number(values.memberSize);

    if (!Number.isFinite(memberSize) || memberSize < 0) {
      return "Membership Size must be zero or greater.";
    }
  }

  return null;
};

const buildExportInput = ({
  chamberId,
  report,
  values,
}: {
  chamberId: string;
  report: ReportDefinition;
  values: ReportFilterValues;
}): ExportCmpReportCsvInput => {
  const input: ExportCmpReportCsvInput = {
    chamberId,
    reportType: report.reportType,
    startDateUtcMilli: toUtcDayStart(values.startDate),
    endDateUtcMilli: toUtcDayEnd(values.endDate),
    compareStartDateUtcMilli: toUtcDayStart(values.compareStartDate),
    compareEndDateUtcMilli: toUtcDayEnd(values.compareEndDate),
  };

  if (
    reportHasFilter({ report, filter: "date" }) &&
    values.filterDate
  ) {
    input.filterDateUtcMilli = toUtcDayStart(values.filterDate);
  }

  if (
    (reportHasFilter({ report, filter: "membershipType" }) ||
      reportHasFilter({ report, filter: "membershipTier" })) &&
    values.membershipTypeId
  ) {
    input.membershipTypeId = values.membershipTypeId;
  }

  if (reportHasFilter({ report, filter: "memberSize" }) && values.memberSize) {
    input.memberSize = Number(values.memberSize);
  }

  if (
    reportHasFilter({ report, filter: "membershipCategory" }) &&
    values.membershipCategory
  ) {
    input.membershipCategory = values.membershipCategory;
  }

  if (reportHasFilter({ report, filter: "eventCode" }) && values.eventCode) {
    input.eventCode = values.eventCode;
  }

  if (reportHasFilter({ report, filter: "eventName" }) && values.eventId) {
    input.eventId = values.eventId;
  }

  if (reportHasFilter({ report, filter: "eventType" }) && values.eventTypeId) {
    input.eventTypeId = values.eventTypeId;
  }

  if (
    reportHasFilter({ report, filter: "sponsorshipLevel" }) &&
    values.sponsorshipPackageId
  ) {
    input.sponsorshipPackageId = values.sponsorshipPackageId;
  }

  if (reportHasFilter({ report, filter: "ticketType" }) && values.ticketTypeId) {
    input.ticketTypeId = values.ticketTypeId;
  }

  if (
    reportHasFilter({ report, filter: "advertisingCode" }) &&
    values.advertisingCode
  ) {
    input.advertisingCode = values.advertisingCode;
  }

  if (
    reportHasFilter({ report, filter: "advertisingOptions" }) &&
    values.advertisingOptionId
  ) {
    input.advertisingOptionId = values.advertisingOptionId;
  }

  if (
    reportHasFilter({ report, filter: "salesAccount" }) &&
    values.salesAccountNumber
  ) {
    input.salesAccountNumber = values.salesAccountNumber;
  }

  if (
    reportHasFilter({ report, filter: "leadCapture" }) &&
    values.leadCaptureId
  ) {
    input.leadCaptureId = values.leadCaptureId;
  }

  if (
    reportHasFilter({ report, filter: "createdBy" }) &&
    values.createdByName
  ) {
    input.createdByName = values.createdByName;
  }

  return input;
};

const Reports = () => {
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [selectedReport, setSelectedReport] = useState<ReportDefinition | null>(
    REPORT_DEFINITIONS[0],
  );
  const [filterValues, setFilterValues] = useState<ReportFilterValues>(
    createDefaultFilterValues,
  );
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const exportInFlightRef = useRef(false);
  const headerOverride = useMemo(
    () => ({
      descriptionClassName: "mt-2 text-base text-[#65748A]",
    }),
    [],
  );

  const {
    data: lookupsData,
    loading: lookupsLoading,
    error: lookupsError,
    refetch: refetchLookups,
  } = useGetCmpReportLookupsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [exportReport, { loading: exportLoading }] =
    useExportCmpReportCsvLazyQuery({ fetchPolicy: "no-cache" });

  const handleReportChange = (report: ReportDefinition | null) => {
    if (report?.id === selectedReport?.id) {
      return;
    }

    setSelectedReport(report);
    setFilterValues(createDefaultFilterValues());
    setStatusMessage(null);
  };

  const handleDownload = async () => {
    if (exportInFlightRef.current) {
      return;
    }

    if (!selectedReport || !chamberId) {
      setStatusMessage({
        severity: "error",
        message: "Select a report after the chamber context has loaded.",
      });
      return;
    }

    const validationError = getFilterValidationError({
      report: selectedReport,
      values: filterValues,
    });

    if (validationError) {
      setStatusMessage({ severity: "error", message: validationError });
      return;
    }

    exportInFlightRef.current = true;

    try {
      setStatusMessage(null);
      const result = await exportReport({
        variables: {
          input: buildExportInput({
            chamberId,
            report: selectedReport,
            values: filterValues,
          }),
        },
      });
      const exportResult = result.data?.exportCmpReportCsv;

      if (!exportResult) {
        throw new Error("The report export did not return a CSV file.");
      }

      downloadTextFile({
        content: exportResult.csvContent,
        contentType: exportResult.contentType,
        filename: exportResult.filename,
      });
      setStatusMessage({
        severity: exportResult.rowCount > 0 ? "success" : "info",
        message:
          exportResult.rowCount > 0
            ? `${exportResult.filename} downloaded with ${exportResult.rowCount} row${
                exportResult.rowCount === 1 ? "" : "s"
              }.`
            : `${exportResult.filename} downloaded with column headings; no records matched the selected filters.`,
      });
    } catch (error) {
      setStatusMessage({
        severity: "error",
        message:
          error instanceof Error
            ? error.message
            : "The report could not be downloaded. Please try again.",
      });
    } finally {
      exportInFlightRef.current = false;
    }
  };

  return (
    <ManagePageShell
      headerOverride={headerOverride}
      statusMessage={statusMessage}
      onStatusMessageClose={() => setStatusMessage(null)}
      statusMessageClassName="mx-4 mb-5 md:mx-0"
      className="w-full pb-0"
    >
      <div
        className="-mx-0 -mt-2 flex overflow-x-auto border-b border-[#DCE4F0] bg-white px-4 md:-mx-8 md:px-8"
        role="tablist"
        aria-label="Report sections"
      >
        <button
          id="general-reports-tab"
          type="button"
          role="tab"
          aria-selected="true"
          aria-controls="general-reports-panel"
          className="whitespace-nowrap border-b-2 border-cpaGold px-4 pb-3 pt-2 text-base font-bold text-cpaGold"
        >
          General Reports
        </button>
        <button
          type="button"
          role="tab"
          aria-selected="false"
          aria-disabled="true"
          disabled
          className="ml-8 cursor-not-allowed whitespace-nowrap border-b-2 border-transparent px-4 pb-3 pt-2 text-base font-bold text-black disabled:opacity-100"
        >
          Special Reports (Coming Soon)
        </button>
      </div>

      <div className="min-h-[calc(100vh-12rem)] bg-[#F7F9FC] px-4 pb-12 pt-10 md:-mx-8 md:px-8">
        <div className="mx-auto w-full max-w-[1320px]">
          <div className="mb-10 flex justify-end">
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={
                !selectedReport ||
                !chamberId ||
                lookupsLoading ||
                exportLoading
              }
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-cpaGold px-5 py-2.5 text-base font-bold text-white shadow-sm transition hover:bg-cpaGoldDark focus:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {exportLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DownloadOutlinedIcon sx={{ fontSize: 20 }} />
              )}
              {exportLoading ? "Preparing CSV..." : "Download CSV"}
            </button>
          </div>

          {!chamberId && (
            <Alert severity="info" className="mb-5">
              Loading chamber report access...
            </Alert>
          )}

          {lookupsError && (
            <Alert
              severity="error"
              className="mb-5"
              action={
                <button
                  type="button"
                  className="font-bold underline"
                  onClick={() => void refetchLookups()}
                >
                  Retry
                </button>
              }
            >
              Report filter options could not be loaded. Retry before
              downloading a filtered report.
            </Alert>
          )}

          <section
            id="general-reports-panel"
            role="tabpanel"
            aria-labelledby="general-reports-tab"
            aria-label="General report builder"
            className="grid min-h-[700px] grid-cols-1 gap-8 rounded-2xl border border-[#8F969E] bg-white px-5 py-7 shadow-sm lg:grid-cols-[minmax(0,1.3fr)_minmax(360px,1fr)] lg:px-8 lg:py-8"
          >
            <ReportSelector
              selectedReport={selectedReport}
              onChange={handleReportChange}
              disabled={!chamberId}
            />

            <div className="min-w-0">
              {lookupsLoading && (
                <div
                  className="mb-4 flex items-center gap-2 text-sm text-gray-600"
                  role="status"
                >
                  <CircularProgress size={16} />
                  Loading report filter options...
                </div>
              )}

              {selectedReport ? (
                <ReportFilters
                  definition={selectedReport}
                  values={filterValues}
                  onChange={setFilterValues}
                  lookups={lookupsData?.getCmpReportLookups}
                  lookupsLoading={lookupsLoading}
                />
              ) : (
                <div className="flex min-h-72 items-center justify-center rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-600">
                  Select a general report to see its available filters.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </ManagePageShell>
  );
};

export default Reports;
